import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ComboboxProps, ComboboxOption } from '../../../types/DesignSystem/Combobox';
import FieldTitle from '../FieldTitle/FieldTitle';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import Tag from '../Tag/Tag';
import Spinner from '../Spinner/Spinner';
import { Add, Check, ChevronDown, Cross } from '../Iconography/Icons';
import { FIELD_BOX, fieldBorder, FIELD_REST_WITHIN, FIELD_DISABLED } from '../fieldChrome';
import { useDebouncedCallback } from '../debounce';
import { FIELD_INSET } from '../controlSize';
import { ControlSize } from '../../../types/DesignSystem/controlSize';
import { useDismissable } from '../useDismissable';
import { useMergedRef } from '../useMergedRef';
import { MOTION } from '../motion';
import { usePresence } from '../usePresence';
import { POPOVER_ROOM_PX, fixedAnchorStyle, preferUpward, samePosition, useAnchorUpdates } from '../useAnchorUpdates';
import { useControlSize } from '../DensityProvider/DensityProvider';
import { RESULTS_ANNOUNCE_MS, announceResultCount, useSettledWords } from '../liveRegion';

/**
 * Combobox (§ 210) — see the type file for when to reach for this vs `Dropdown`.
 *
 * **The keyboard model is `aria-activedescendant`, not roving focus.** Focus
 * never leaves the input: arrows move a VIRTUAL highlight the input reports via
 * `aria-activedescendant`, so the user can keep typing mid-navigation — the
 * defining combobox behaviour, and the reason this does not reuse
 * `listKeyboardNav` (which moves real DOM focus, correct for `Menu` and
 * `Dropdown`'s listbox where there is nothing to type into).
 *
 * **Options are `<div role="option">`, not buttons** — a focusable option would
 * fight the model above. `onMouseDown` preventDefault keeps the input focused
 * through a click, so selection never blurs the field.
 *
 * § 304 — the list is PORTALLED by default (`usePortal`), placed by the shared
 * `fixedAnchorStyle` engine and re-measured while open; `usePortal={false}` is the
 * inline-absolute path this component shipped with in § 210.
 */

/*
 * CONTROL_PX as MIN heights (literals — the class scanner cannot see a template):
 * single-line the field sits exactly on the control grid; multi grows when the
 * chips wrap. `CONTROL_HEIGHT`'s fixed heights would clip the second chip row.
 */
const MIN_H: Record<ControlSize, string> = {
  sm: 'tw-min-h-[28px]',
  md: 'tw-min-h-[36px]',
  lg: 'tw-min-h-[44px]',
};

/* Same surface as Dropdown's popover (§ 51 — one popover chrome, everywhere).
   Origin and motion are per-state now (§ 242): the list flips above the field when
   the viewport runs out below (§ 230 noted this path did not exist), grows out of
   whichever edge it opens from, and folds back on exit. */
const POPOVER_CHROME =
  'tw-bg-surface-raised tw-border tw-border-edge tw-rounded-srf tw-shadow-ds-md';

/* § 396 — the result-count clock and words live in `liveRegion.tsx` since § 406, shared with
   Dropdown's filter; re-exported so an import from here keeps working. */
export { RESULTS_ANNOUNCE_MS };

/** The multi-select's empty default — one reference, so a render never mints a new array. */
const NO_VALUES: string[] = [];

const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(function Combobox({
  label,
  options,
  value: valueProp,
  defaultValue,
  onChange,
  multiple = false,
  values: valuesProp,
  defaultValues,
  onValuesChange,
  onSearch,
  debounceMs,
  clearable = false,
  creatable = false,
  onCreate,
  createLabel = (query: string) => `Create "${query}"`,
  loading = false,
  placeholder = 'Search…',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  size: sizeProp,
  labelSize,
  usePortal = true,
  noResultsText = 'No matching options',
  resultsAnnouncement = announceResultCount,
  renderOption,
  className = '',
  id,
  name,
  'data-testid': dataTestId,
  'aria-describedby': describedByProp,
  ...rest
}, forwardedRef) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  const uid = React.useId();
  const inputId = id ?? `${uid}-input`;
  const labelId = `${uid}-label`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  /* § 356 / § 357 — see TextField. Legacy defaults kept: `value` null, `values` []. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState<string | null>(valueProp, defaultValue, null);
  const [values, setValues] = useControllableState<string[]>(valuesProp, defaultValues, NO_VALUES);
  const listboxId = `${uid}-listbox`;
  const optionId = (index: number) => `${uid}-opt-${index}`;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /* § 242 — held for its exit, aria-hidden and inert while leaving; flips upward
     when the field sits near the bottom of the viewport, re-measured while open. */
  const { isPresent, phase } = usePresence(isOpen && !disabled, MOTION.popover.exitMs);
  const exiting = phase === 'exiting';
  const [openUp, setOpenUp] = useState(false);
  /* § 304 — the fixed placement of the portalled list, sized to the field. */
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const measure = useCallback(() => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect) return;
    const above = preferUpward(rect, POPOVER_ROOM_PX);
    setOpenUp(above);
    if (usePortal) {
      // § 334 — the room cap and the clipped-trigger hide, as Dropdown.
      const next = fixedAnchorStyle(rect, { above, matchWidth: true, fit: true, anchorEl: fieldRef.current });
      setMenuStyle((prev) => (prev && samePosition(prev, next) ? prev : next));
    }
  }, [usePortal]);
  useAnchorUpdates(isPresent && !exiting, fieldRef, measure);
  const setInputRef = useMergedRef<HTMLInputElement>(inputRef, forwardedRef);
  const activeOptionRef = useRef<HTMLDivElement>(null);

  const selectedOption = multiple ? null : options.find((opt) => opt.value === value) ?? null;
  const selectedValues = multiple ? values : [];

  /* § 303 — the one debounced channel to `onSearch`: `call` while typing (synchronous
     when `debounceMs` is unset — the behaviour every consumer has today), `now` on
     the resets, which must reach the server at once and cancel a pending query. */
  const search = useDebouncedCallback<[string]>(onSearch, debounceMs);
  const hasSelection = multiple ? selectedValues.length > 0 : value !== null && value !== undefined;
  const clearSelection = () => {
    if (multiple) {
      setValues([]);
      onValuesChange?.([]);
    } else {
      setValue(null);
      onChange?.(null, null);
    }
    if (query !== '') {
      setQuery('');
      search.now('');
    }
  };

  /* `onSearch` means the SERVER did the matching — re-filtering its answer
     locally is a silent narrowing (the § 65 lesson, restated in the type file). */
  const trimmedQuery = query.trim().toLowerCase();
  const visibleOptions = !onSearch && trimmedQuery
    ? options.filter((option) => option.label.toLowerCase().includes(trimmedQuery))
    : options;

  const isSelected = (option: ComboboxOption) =>
    multiple ? selectedValues.includes(option.value) : option.value === value;

  /* § 305 — the create row: offered while the query matches no label EXACTLY (a
     partial match is a filter result, not a new value). It takes the index after the
     last option, so the highlight, `aria-activedescendant` and Enter all see one list. */
  const createQuery = query.trim();
  const showCreate = creatable
    && createQuery !== ''
    && !options.some((option) => option.label.trim().toLowerCase() === createQuery.toLowerCase());
  const createIndex = visibleOptions.length;
  const rowCount = visibleOptions.length + (showCreate ? 1 : 0);

  /*
    § 396 — the result count, SPOKEN. Typing filtered the list in silence: a sighted user sees
    it shrink to three rows or to "No matching options", and a screen-reader user heard only
    their own letters and had to arrow into the list to learn whether anything matched. The
    count goes to a status region that is mounted with the field (below) — so it is a change
    the screen reader hears, not a region arriving with its words — once the query has been
    still for RESULTS_ANNOUNCE_MS. Not while loading (the count is not known yet), and never
    for an empty query (nothing was asked). A creatable field says its create row too.
  */
  const resultsWords = isOpen && !loading && query.trim() !== ''
    ? `${resultsAnnouncement(visibleOptions.length)}${showCreate ? `. ${createLabel(createQuery)}` : ''}`
    : '';
  const resultsSpoken = useSettledWords(resultsWords);

  const firstEnabled = (from: number, direction: 1 | -1): number => {
    for (
      let i = from;
      i >= 0 && i < rowCount;
      i += direction
    ) {
      if (i === createIndex || !visibleOptions[i].disabled) return i;
    }
    return -1;
  };

  const open = () => {
    if (disabled || isOpen) return;
    measure();
    const selectedIdx = multiple ? -1 : visibleOptions.findIndex((o) => o.value === value && !o.disabled);
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : firstEnabled(0, 1));
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
    /* Reset on close so reopening always shows the full list — the stale-filter
       "where did my options go" (Dropdown § 65). For async the reset must reach
       the server too, or the empty input would sit over last query's answer. */
    if (query !== '') {
      setQuery('');
      search.now('');
    }
  };

  useDismissable({
    isOpen,
    onDismiss: close,
    /* § 304 — the portalled list is not a DOM descendant of the field; its marker
       (this control's own id) says it is inside. */
    isInside: (target) =>
      !!containerRef.current?.contains(target) ||
      (usePortal && !!(target as Element).closest?.(`[data-dropdown-portal="${uid}"]`)),
  });

  /* The filter can shrink the list under the highlight — reseat it. NUL as the
     separator because a value can contain any visible character; written as the
     ESCAPE, never a raw byte — the original shipped an actual 0x00 here, which
     made git treat this entire file as binary (unreviewable diffs) while
     rendering as a space in every editor (§ 230). */
  const visibleKey = visibleOptions.map((o) => o.value).join('\0');
  React.useEffect(() => {
    if (!isOpen) return;
    if (activeIndex >= rowCount || (activeIndex >= 0 && activeIndex < visibleOptions.length && visibleOptions[activeIndex]?.disabled)) {
      setActiveIndex(firstEnabled(0, 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visibleKey stands in for visibleOptions
  }, [isOpen, visibleKey, showCreate]);

  React.useEffect(() => {
    // jsdom has no scrollIntoView; the guard is the difference between a test
    // suite and a TypeError.
    activeOptionRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const select = (option: ComboboxOption) => {
    if (option.disabled) return;
    if (multiple) {
      const next = selectedValues.includes(option.value)
        ? selectedValues.filter((v) => v !== option.value)
        : [...selectedValues, option.value];
      setValues(next);
      onValuesChange?.(next);
      /* The list stays open — a multi-select is a session, not a pick. The query
         clears so the next name starts fresh (and the server hears the reset). */
      if (query !== '') {
        setQuery('');
        search.now('');
      }
      inputRef.current?.focus();
    } else {
      setValue(option.value);
      onChange?.(option.value, option);
      close();
    }
  };

  const removeValue = (removed: string) => {
    const next = selectedValues.filter((v) => v !== removed);
    setValues(next);
    onValuesChange?.(next);
  };

  /* § 305 — the caller owns the list, so it adds the option and selects it; this
     component only reports the query and treats the row like a pick. */
  const create = () => {
    if (!showCreate) return;
    onCreate?.(createQuery);
    if (multiple) {
      setQuery('');
      search.now('');
      inputRef.current?.focus();
    } else {
      close();
    }
  };

  const moveActive = (direction: 1 | -1) => {
    const start = activeIndex < 0
      ? (direction === 1 ? 0 : rowCount - 1)
      : activeIndex + direction;
    const next = firstEnabled(start, direction);
    if (next >= 0) setActiveIndex(next);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) open();
      else moveActive(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter') {
      if (!isOpen) return;
      // The list is open: Enter commits the highlight, never submits the form.
      event.preventDefault();
      if (showCreate && activeIndex === createIndex) {
        create();
        return;
      }
      const active = visibleOptions[activeIndex];
      if (active) select(active);
      return;
    }
    if (event.key === 'Backspace' && multiple && query === '' && selectedValues.length > 0) {
      removeValue(selectedValues[selectedValues.length - 1]);
    }
    /* § 303 — the keyboard's clear for a single value, so `clearable` never needs a
       second tab stop: Backspace on an empty query, the same gesture multi uses. */
    if (event.key === 'Backspace' && !multiple && clearable && query === '' && hasSelection) {
      event.preventDefault();
      clearSelection();
    }
    /* Escape is deliberately NOT handled here: `useDismissable` owns it, with
       its layer stack — so an open combobox inside a Dialog closes itself on
       the first Escape and the Dialog on the second, never both at once. */
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setQuery(next);
    search.call(next);
    if (!isOpen) open();
  };

  /* What the input SHOWS: while open (or in multi mode) it is the query; closed
     single-select shows the chosen label, and the placeholder carries the chosen
     label while the query is being typed so the context never vanishes. */
  const inputValue = multiple || isOpen ? query : selectedOption?.label ?? '';
  const inputPlaceholder = multiple
    ? (selectedValues.length > 0 ? '' : placeholder)
    : selectedOption?.label ?? placeholder;

  const chipFor = (chosen: string) => {
    const option = options.find((o) => o.value === chosen);
    return (
      <Tag
        key={chosen}
        label={option?.label ?? chosen}
        disabled={disabled}
        data-testid={dataTestId ? `${dataTestId}-tag-${chosen}` : undefined}
        onRemove={(event) => {
          // The chip sits inside the click-to-open field row; removal is not opening.
          event.stopPropagation();
          removeValue(chosen);
        }}
      />
    );
  };

  return (
    <div ref={containerRef} className={className}>
      {label !== '' && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it stays the input's accessible name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          disabled={disabled}
          htmlFor={inputId}
          labelId={labelId}
        />
      )}

      <div className="tw-relative">
        {/*
          The BORDER lives on this wrapper, not on the input — chips, input and
          chevron share one bordered row, so the focus treatment is
          `focus-within` (`FIELD_REST_WITHIN`), the Search arrangement (§ 29).
          `fieldBorder` owns the border colour so error/open/disabled/rest never
          stack. `min-h`, not the control grid's fixed `h`: chips wrap.
        */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- a caught click only forwards focus to the input, which owns the keyboard */}
        <div
          ref={fieldRef}
          data-testid={dataTestId ? `${dataTestId}-field` : undefined}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
            open();
          }}
          className={`tw-w-full ${MIN_H[size]} ${FIELD_INSET[size]} tw-py-1 ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled, open: isOpen })} tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-body-primary
            ${disabled ? `${FIELD_DISABLED}` : `tw-cursor-text ${FIELD_REST_WITHIN}`}
            ${isOpen && !disabled
              ? (invalid ? 'tw-ring-1 tw-ring-tone-danger-glyph' : 'tw-ring-1 tw-ring-accent-hover')
              : ''
            }
          `}
        >
          {multiple && selectedValues.map(chipFor)}
          <input
            /* rest first — the Button.tsx rule. This input owns value, change,
               and the combobox keyboard; a stray caller handler must not
               silently replace them. */
            {...rest}
            ref={setInputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
            aria-required={required || undefined}
            aria-invalid={invalid || undefined}
            /* Composed with a caller's own `aria-describedby`, which the rest spread used to
               hand in only for this attribute to overwrite it (the TextField rule). */
            aria-describedby={[describedByProp, ...messageIds({ helperId, errorId }, { helperText, errorMessage })]
              .filter(Boolean).join(' ') || undefined}
            disabled={disabled}
            value={inputValue}
            placeholder={inputPlaceholder}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={(event) => {
              if (!containerRef.current?.contains(event.relatedTarget as Node)) close();
            }}
            data-testid={dataTestId}
            className={`tw-flex-1 tw-min-w-[60px] tw-bg-transparent tw-border-0 tw-outline-none tw-p-0 tw-text-body-primary placeholder:tw-text-ink-secondary
              ${disabled ? 'tw-text-ink-disabled tw-cursor-not-allowed' : 'tw-text-ink'}`}
          />
          {/* § 303 — the clear ×, a mouse affordance like the chevron beside it (the
              keyboard clears with Backspace); shown only while there is something to
              clear. The input keeps focus through the click. */}
          {clearable && hasSelection && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Clear"
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                clearSelection();
                inputRef.current?.focus();
              }}
              className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-ctl tw-bg-transparent tw-border-0 tw-p-0 tw-text-ink-tertiary hover:tw-text-ink motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift"
            >
              <Cross size={16} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            /* Mouse-only affordance: the input already owns the keyboard (the
               combobox pattern has ONE tab stop), so this leaves the tab order.
               `onMouseDown` preventDefault keeps the input focused through it. */
            tabIndex={-1}
            aria-label={isOpen ? 'Close options' : 'Open options'}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) return;
              if (isOpen) close();
              else {
                inputRef.current?.focus();
                open();
              }
            }}
            className="tw-flex tw-items-center tw-flex-shrink-0 tw-bg-transparent tw-border-0 tw-p-0"
          >
            <ChevronDown
              aria-hidden="true"
              className={`motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${
                disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'
              } ${isOpen ? 'tw-rotate-180' : ''}`}
            />
          </button>
        </div>

        {isPresent && (!usePortal || menuStyle !== null) && (usePortal ? createPortal : ((node: React.ReactNode) => node))(
          <div
            id={listboxId}
            role="listbox"
            aria-hidden={exiting || undefined}
            aria-multiselectable={multiple || undefined}
            aria-labelledby={label !== '' ? labelId : undefined}
            data-testid={dataTestId ? `${dataTestId}-list` : undefined}
            /* § 304 — portalled: the marker (this control's id) for the guards, the
               fixed style for the place; the origin still follows the flip. Inline:
               the § 210 geometry, unchanged. */
            data-dropdown-portal={usePortal ? uid : undefined}
            style={usePortal ? menuStyle ?? undefined : undefined}
            /* `overscroll-contain` (§ 222): the option list's wheel must not chain
               into the page under the popover. */
            className={`${POPOVER_CHROME} ${usePortal ? '' : 'tw-absolute tw-left-0 tw-right-0 tw-z-10'} ${
              openUp
                ? (usePortal ? 'tw-origin-bottom' : 'tw-bottom-full tw-mb-1 tw-origin-bottom')
                : (usePortal ? 'tw-origin-top' : 'tw-top-full tw-mt-1 tw-origin-top')
            } ${exiting ? `${MOTION.popover.exit} tw-pointer-events-none` : MOTION.popover.enter} tw-max-h-[min(16rem,var(--ds-popover-room,16rem))] tw-overflow-y-auto tw-overscroll-contain`}
          >
            {visibleOptions.map((option, index) => {
              const selected = isSelected(option);
              const active = index === activeIndex;
              return (
                <div
                  key={option.value}
                  id={optionId(index)}
                  ref={active ? activeOptionRef : undefined}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={option.disabled || undefined}
                  data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(option)}
                  /* Selected is BRAND, the keyboard highlight is the neutral
                     hover ground — the § 51 split, with the highlight and the
                     hover deliberately the SAME ground so mouse and keyboard
                     read as one. Branches are exclusive: two plain bg classes
                     on one row would leave the winner to stylesheet order.
                     § 296 — the highlight is `surface-hover-strong`, the ground
                     the hover branch below actually uses since § 256. It was
                     `surface-sunken`: one hex in light, and in dark the PAGE
                     ground — arrowing the list sank the highlight −8.7 L* while
                     the pointer lifted a row +3.3, opposite directions for what
                     this comment had always called one ground. */
                  className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-body-primary tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift
                    ${option.disabled
                      ? 'tw-text-ink-disabled tw-cursor-not-allowed'
                      : selected
                        ? 'tw-bg-accent-tint tw-text-accent tw-cursor-pointer'
                        : active
                          ? 'tw-bg-surface-hover-strong tw-text-ink tw-cursor-pointer'
                          : 'tw-text-ink-secondary hover:tw-bg-surface-hover-strong active:tw-bg-tone-neutral-tint hover:tw-text-ink tw-cursor-pointer'
                    }`}
                >
                  {/* § 385 — the caller's content, inside the component's row. A custom row is not
                      forced onto one truncated line: it may carry a second line of its own. */}
                  <span className={renderOption ? 'tw-min-w-0 tw-flex-1' : 'tw-truncate tw-min-w-0'}>
                    {renderOption
                      ? renderOption(option, { selected, active, disabled: Boolean(option.disabled) })
                      : option.label}
                  </span>
                  {selected && (
                    /* § 232 — the set's Check (see Dropdown's option row). */
                    <Check size={12} aria-hidden="true" className="tw-ml-2 tw-flex-shrink-0" />
                  )}
                </div>
              );
            })}
            {showCreate && (
              <div
                id={optionId(createIndex)}
                ref={activeIndex === createIndex ? activeOptionRef : undefined}
                role="option"
                aria-selected={false}
                data-testid={dataTestId ? `${dataTestId}-create` : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={create}
                /* The same row grammar as an option, in the accent ink: it is an ACTION
                   in a list of values, and the glyph says so before the words do. */
                className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-body-primary tw-cursor-pointer tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
                  activeIndex === createIndex
                    ? 'tw-bg-surface-hover-strong tw-text-accent'
                    : 'tw-text-accent hover:tw-bg-surface-hover-strong active:tw-bg-tone-neutral-tint'
                }`}
              >
                <Add size={12} aria-hidden="true" className="tw-flex-shrink-0" />
                <span className="tw-truncate tw-min-w-0">{createLabel(createQuery)}</span>
              </div>
            )}
            {visibleOptions.length === 0 && !loading && !showCreate && (
              <div
                className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-tertiary"
                data-testid={dataTestId ? `${dataTestId}-no-options` : undefined}
              >
                {noResultsText}
              </div>
            )}
            {loading && (
              <div
                className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-tertiary tw-flex tw-items-center tw-justify-center tw-gap-2"
                data-testid={dataTestId ? `${dataTestId}-loading` : undefined}
              >
                <Spinner size="sm" aria-hidden="true" />
                Loading...
              </div>
            )}
          </div>,
          document.body,
        )}
      </div>

      {/* § 396 — mounted with the field and empty until a query has settled: see `resultsWords`. */}
      <span role="status" className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-status` : undefined}>
        {resultsSpoken}
      </span>

      {/* § 302 — the one helper seam: an errored Combobox announces (role="alert",
          remounted) like every other field, where it used to stay silent until focus. */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />

      {/* The visible input holds the QUERY, so a form must not submit it as the
          value — the hidden inputs carry the actual selection under `name`. */}
      {name && !multiple && <input type="hidden" name={name} value={value ?? ''} />}
      {name && multiple && selectedValues.map((v) => (
        <input key={v} type="hidden" name={name} value={v} />
      ))}
    </div>
  );
});

export default Combobox;
